/**
 * NotificacoesPedidos — Toast in-app pra novos pedidos delivery (D-96, 2026-05-14).
 *
 * Escuta o CustomEvent `myros-novo-pedido` despachado pelo hook useNovosPedidos.
 * Pra cada evento:
 *   - Adiciona toast no canto inferior direito (max 3 simultâneos, FIFO).
 *   - Toca som curto via notifSound.
 *   - Auto-dismiss em 12s.
 *   - Click → navega pra /admin/delivery/pedidos?focus=<id>.
 *
 * Registrado em App.tsx, fora de Routes, só renderiza se user é caixa/admin.
 * Visual: card discreto branco com avatar terracota, animação de fade-in.
 */
import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser } from '../App'
import { useNovosPedidos } from '../hooks/useNovosPedidos'
import {
  playNotif,
  isNotifMuted,
  setNotifMuted,
  showBrowserNotif,
  browserNotifEnabled,
} from '../utils/notifSound'
import Icon from './Icon'
import type { DeliveryOrder } from '../api/deliveryClient'

interface ToastItem {
  id: string
  order: DeliveryOrder
  timeout: any
}

const MAX_TOASTS = 3
const DISMISS_MS = 12_000

const fmt = (cents: number) => `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`

export default function NotificacoesPedidos() {
  const { user } = useUser()
  const nav = useNavigate()
  const isAuth = !!user && (user.perfil === 'caixa' || user.perfil === 'admin')

  // Hook polling — só dispara eventos quando autorizado
  useNovosPedidos(isAuth)

  const [toasts, setToasts] = useState<ToastItem[]>([])
  const [muted, setMutedState] = useState(isNotifMuted())

  const dismiss = useCallback((toastId: string) => {
    setToasts(prev => {
      const found = prev.find(t => t.id === toastId)
      if (found) clearTimeout(found.timeout)
      return prev.filter(t => t.id !== toastId)
    })
  }, [])

  useEffect(() => {
    if (!isAuth) return
    function onNovo(e: Event) {
      const ce = e as CustomEvent<DeliveryOrder>
      const order = ce.detail
      if (!order) return
      const toastId = `${order.id}-${Date.now()}`
      const timeout = setTimeout(() => dismiss(toastId), DISMISS_MS)
      setToasts(prev => {
        // Dedup: se já existe toast pra esse pedido, descarta esse novo
        if (prev.some(t => String(t.order.id) === String(order.id))) {
          clearTimeout(timeout)
          return prev
        }
        const next = [...prev, { id: toastId, order, timeout }]
        // Limita a MAX_TOASTS — descarta os mais antigos
        while (next.length > MAX_TOASTS) {
          const oldest = next.shift()
          if (oldest) clearTimeout(oldest.timeout)
        }
        return next
      })
      playNotif()
      // D-96 iter 2: notif nativa do OS quando aba está em background
      if (document.hidden && browserNotifEnabled()) {
        const titulo = `Novo pedido — ${order.clienteNome || 'cliente'}`
        const corpo = `Pedido #${order.orderNumber} · ${fmt(order.totalCents)}`
        showBrowserNotif(titulo, corpo, () => {
          window.location.href = `/admin/delivery/pedidos?focus=${order.id}`
        })
      }
    }
    window.addEventListener('myros-novo-pedido', onNovo as EventListener)
    return () => window.removeEventListener('myros-novo-pedido', onNovo as EventListener)
  }, [isAuth, dismiss])

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => { toasts.forEach(t => clearTimeout(t.timeout)) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function toggleMute() {
    const next = !muted
    setNotifMuted(next)
    setMutedState(next)
  }

  function abrirPedido(order: DeliveryOrder, toastId: string) {
    dismiss(toastId)
    nav(`/admin/delivery/pedidos?focus=${order.id}`)
  }

  if (!isAuth) return null

  return (
    <div className="fixed bottom-3 right-3 z-[60] flex flex-col items-end gap-2 pointer-events-none">
      {toasts.map(t => {
        const o = t.order
        const nome = o.clienteNome || `Pedido #${o.orderNumber}`
        return (
          <button
            key={t.id}
            onClick={() => abrirPedido(o, t.id)}
            className="pointer-events-auto group max-w-sm w-[300px] sm:w-[340px] bg-white border border-gray-200 shadow-lg rounded-xl p-3 flex items-start gap-3 hover:shadow-xl hover:border-terracota/40 transition text-left"
            style={{ animation: 'myros-toast-in 220ms ease-out' }}
          >
            <div className="shrink-0 w-9 h-9 rounded-full bg-terracota/10 text-terracota flex items-center justify-center">
              <Icon name="bell" size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-carvao">
                <span className="font-semibold">{nome}</span>
                <span className="text-gray-600"> fez um pedido</span>
              </p>
              <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                <span className="font-mono tabular-nums text-terracota">{fmt(o.totalCents)}</span>
                <span>·</span>
                <span>#{o.orderNumber}</span>
              </p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); dismiss(t.id) }}
              className="shrink-0 w-7 h-7 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center"
              aria-label="Dispensar"
            ><Icon name="close" size={14} /></button>
          </button>
        )
      })}
      {/* Toggle de mute — visível só quando há toasts (não polui canto vazio) */}
      {toasts.length > 0 && (
        <button
          onClick={toggleMute}
          className="pointer-events-auto text-[10px] uppercase tracking-wider text-gray-500 hover:text-terracota bg-white/80 backdrop-blur px-2 py-1 rounded-full border border-gray-200"
          title={muted ? 'Som mudo — clique para ativar' : 'Som ativo — clique para silenciar'}
        >
          {muted ? 'Som mudo' : 'Som ativo'}
        </button>
      )}

      {/* keyframes injetados inline (sem custom CSS file) */}
      <style>{`
        @keyframes myros-toast-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}
